import React, { useState, useEffect, useCallback } from 'react';
import {
  TrendingUp, TrendingDown, DollarSign, Briefcase, Wallet,
  ArrowUpRight, ArrowDownRight, RefreshCw, Settings, Building2,
  AlertCircle, CheckCircle, Loader, ShoppingCart, Clock,
  ChevronRight, Shield, Zap, PieChart, BarChart3, Plus,
  ArrowDownCircle, ArrowUpCircle, User, CreditCard, History,
  ExternalLink, Target, Activity, Percent, Award, Search,
  Scale, Lightbulb, AlertTriangle
} from 'lucide-react';

// Import sub-components
import BrokerOnboarding from './BrokerOnboarding';
import BankFunding from './BankFunding';
import StockSearch from './StockSearch';

// Import analytics components
import AdvancedPortfolioAnalytics from './AdvancedPortfolioAnalytics';
import PortfolioRebalancer from './PortfolioRebalancer';
import WhatIfAnalyzer from './WhatIfAnalyzer';
import PortfolioRiskAnalyzer from './PortfolioRiskAnalyzer';

const API_URL = process.env.REACT_APP_API_URL || 'https://financial-analyst-backend-production-7175.up.railway.app/api';

// ============================================
// ACCOUNT STATUS BADGE
// ============================================
const AccountStatusBadge = ({ status }) => {
  const configs = {
    ACTIVE: { color: '#22c55e', bg: 'rgba(34, 197, 94, 0.2)', label: 'Active' },
    PENDING: { color: '#fbbf24', bg: 'rgba(251, 191, 36, 0.2)', label: 'Pending Review' },
    APPROVED: { color: '#22c55e', bg: 'rgba(34, 197, 94, 0.2)', label: 'Approved' },
    SUBMITTED: { color: '#60a5fa', bg: 'rgba(96, 165, 250, 0.2)', label: 'Submitted' },
    ACTION_REQUIRED: { color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.2)', label: 'Action Required' },
    REJECTED: { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.2)', label: 'Rejected' }
  };
  
  const config = configs[status] || configs.PENDING;
  
  return (
    <span style={{
      background: config.bg,
      color: config.color,
      padding: '0.25rem 0.75rem',
      borderRadius: '1rem',
      fontSize: '0.75rem',
      fontWeight: '600',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.25rem'
    }}>
      {status === 'ACTIVE' && <CheckCircle size={12} />}
      {config.label}
    </span>
  );
};

// ============================================
// MINI PORTFOLIO CHART (Donut)
// ============================================
const PortfolioDonut = ({ positions }) => {
  if (!positions || positions.length === 0) return null;
  
  const total = positions.reduce((sum, p) => sum + (p.marketValue || 0), 0);
  const colors = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#14b8a6', '#f97316', '#ec4899'];
  
  let currentAngle = 0;
  const segments = positions.slice(0, 8).map((p, idx) => {
    const percent = (p.marketValue || 0) / total;
    const angle = percent * 360;
    const startAngle = currentAngle;
    currentAngle += angle;
    return { ...p, percent, startAngle, angle, color: colors[idx % colors.length] };
  });

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
      <svg width="80" height="80" viewBox="0 0 100 100">
        {segments.map((seg, idx) => {
          const startRad = (seg.startAngle - 90) * Math.PI / 180;
          const endRad = (seg.startAngle + seg.angle - 90) * Math.PI / 180;
          const largeArc = seg.angle > 180 ? 1 : 0;
          const x1 = 50 + 40 * Math.cos(startRad);
          const y1 = 50 + 40 * Math.sin(startRad);
          const x2 = 50 + 40 * Math.cos(endRad);
          const y2 = 50 + 40 * Math.sin(endRad);
          
          return (
            <path
              key={idx}
              d={`M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArc} 1 ${x2} ${y2} Z`}
              fill={seg.color}
              stroke="#1e293b"
              strokeWidth="1"
            />
          );
        })}
        <circle cx="50" cy="50" r="25" fill="#1e293b" />
      </svg>
      <div style={{ fontSize: '0.75rem' }}>
        {segments.slice(0, 4).map((seg, idx) => (
          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
            <div style={{ width: '8px', height: '8px', borderRadius: '2px', background: seg.color }} />
            <span style={{ color: '#94a3b8' }}>{seg.symbol}</span>
            <span style={{ color: '#e2e8f0', fontWeight: '500' }}>{(seg.percent * 100).toFixed(0)}%</span>
          </div>
        ))}
        {positions.length > 4 && (
          <div style={{ color: '#64748b', fontSize: '0.7rem' }}>+{positions.length - 4} more</div>
        )}
      </div>
    </div>
  );
};

// ============================================
// QUICK TRADE CARD - With Stock Search
// ============================================
const QuickTradeCard = ({ onTrade }) => {
  const popularStocks = ['AAPL', 'TSLA', 'NVDA', 'MSFT', 'AMZN', 'GOOGL', 'META', 'SPY'];
  
  return (
    <div className="trading-section-card" style={{
      background: 'rgba(30, 41, 59, 0.6)',
      border: '1px solid #334155',
      borderRadius: '0.75rem',
      padding: '1.25rem',
      minWidth: 0,
      overflow: 'hidden'
    }}>
      <h3 style={{ 
        color: '#e2e8f0', 
        margin: '0 0 1rem',
        fontSize: '1rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      }}>
        <Zap size={18} color="#fbbf24" />
        Quick Trade
      </h3>
      
      {/* Stock Search */}
      <div style={{ marginBottom: '1rem' }}>
        <StockSearch 
          onSelectStock={(symbol) => onTrade(symbol)}
          placeholder="Search stocks to trade..."
        />
      </div>
      
      {/* Popular Stocks */}
      <div style={{ marginBottom: '0.5rem' }}>
        <span style={{ color: '#64748b', fontSize: '0.75rem' }}>Popular:</span>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
        {popularStocks.map(s => (
          <button
            key={s}
            onClick={() => onTrade(s)}
            style={{
              padding: '0.4rem 0.75rem',
              background: '#334155',
              border: 'none',
              borderRadius: '0.375rem',
              color: '#94a3b8',
              fontSize: '0.8rem',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#475569';
              e.currentTarget.style.color = '#e2e8f0';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#334155';
              e.currentTarget.style.color = '#94a3b8';
            }}
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
};

// ============================================
// POSITION ROW
// ============================================
const PositionRow = ({ position, onTrade }) => {
  const isProfit = position.unrealizedPL >= 0;
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
  
  return (
    <div style={{
      padding: '0.75rem 0',
      borderBottom: '1px solid #334155',
    }}>
      {/* Mobile: Card layout */}
      <div className="position-row-desktop" style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr 1fr auto',
        gap: '1rem',
        alignItems: 'center'
      }}>
        <div>
          <div style={{ color: '#e2e8f0', fontWeight: '600' }}>{position.symbol}</div>
          <div style={{ color: '#64748b', fontSize: '0.8rem' }}>{position.quantity} shares</div>
        </div>
        <div>
          <div style={{ color: '#e2e8f0' }}>${position.currentPrice?.toFixed(2)}</div>
          <div style={{ color: '#64748b', fontSize: '0.8rem' }}>
            Avg: ${position.avgEntryPrice?.toFixed(2)}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ color: '#e2e8f0' }}>${position.marketValue?.toFixed(2)}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ 
            color: isProfit ? '#22c55e' : '#ef4444',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '0.25rem'
          }}>
            {isProfit ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
            ${Math.abs(position.unrealizedPL)?.toFixed(2)}
          </div>
          <div style={{ 
            color: isProfit ? '#22c55e' : '#ef4444',
            fontSize: '0.8rem'
          }}>
            {isProfit ? '+' : ''}{position.unrealizedPLPercent?.toFixed(2)}%
          </div>
        </div>
        <button
          onClick={() => onTrade(position.symbol)}
          style={{
            padding: '0.5rem 1rem',
            background: 'rgba(99, 102, 241, 0.2)',
            border: '1px solid #6366f1',
            borderRadius: '0.375rem',
            color: '#818cf8',
            fontSize: '0.8rem',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          Trade
        </button>
      </div>
      
      {/* Mobile card layout */}
      <div className="position-row-mobile" style={{ display: 'none' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
          <div>
            <div style={{ color: '#e2e8f0', fontWeight: '600', fontSize: '1rem' }}>{position.symbol}</div>
            <div style={{ color: '#64748b', fontSize: '0.75rem' }}>{position.quantity} shares @ ${position.avgEntryPrice?.toFixed(2)}</div>
          </div>
          <button
            onClick={() => onTrade(position.symbol)}
            style={{
              padding: '0.4rem 0.75rem',
              background: 'rgba(99, 102, 241, 0.2)',
              border: '1px solid #6366f1',
              borderRadius: '0.375rem',
              color: '#818cf8',
              fontSize: '0.75rem',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Trade
          </button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem' }}>
          <div>
            <div style={{ color: '#64748b', fontSize: '0.65rem' }}>Price</div>
            <div style={{ color: '#e2e8f0', fontSize: '0.85rem', fontWeight: '500' }}>${position.currentPrice?.toFixed(2)}</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: '#64748b', fontSize: '0.65rem' }}>Value</div>
            <div style={{ color: '#e2e8f0', fontSize: '0.85rem', fontWeight: '500' }}>${position.marketValue?.toFixed(2)}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ color: '#64748b', fontSize: '0.65rem' }}>P/L</div>
            <div style={{ color: isProfit ? '#22c55e' : '#ef4444', fontSize: '0.85rem', fontWeight: '600' }}>
              {isProfit ? '+' : ''}${position.unrealizedPL?.toFixed(2)}
            </div>
            <div style={{ color: isProfit ? '#22c55e' : '#ef4444', fontSize: '0.7rem' }}>
              ({position.unrealizedPLPercent?.toFixed(2)}%)
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// ORDER ROW
// ============================================
const OrderRow = ({ order, onCancel }) => {
  const getStatusStyle = (status) => {
    const styles = {
      filled: { color: '#22c55e', bg: 'rgba(34, 197, 94, 0.2)' },
      partially_filled: { color: '#fbbf24', bg: 'rgba(251, 191, 36, 0.2)' },
      new: { color: '#60a5fa', bg: 'rgba(96, 165, 250, 0.2)' },
      accepted: { color: '#60a5fa', bg: 'rgba(96, 165, 250, 0.2)' },
      pending_new: { color: '#60a5fa', bg: 'rgba(96, 165, 250, 0.2)' },
      canceled: { color: '#94a3b8', bg: 'rgba(148, 163, 184, 0.2)' },
      rejected: { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.2)' }
    };
    return styles[status] || styles.new;
  };
  
  const style = getStatusStyle(order.status);
  const canCancel = ['new', 'accepted', 'pending_new'].includes(order.status);
  
  return (
    <div style={{
      padding: '0.75rem 0',
      borderBottom: '1px solid #334155',
    }}>
      {/* Desktop layout */}
      <div className="order-row-desktop" style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr 1fr auto',
        gap: '1rem',
        alignItems: 'center'
      }}>
        <div>
          <div style={{ 
            color: order.side === 'buy' ? '#22c55e' : '#ef4444',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem'
          }}>
            {order.side === 'buy' ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
            {order.side.toUpperCase()} {order.symbol}
          </div>
          <div style={{ color: '#64748b', fontSize: '0.8rem' }}>
            {order.qty} shares • {order.type}
          </div>
        </div>
        <div>
          <div style={{ color: '#e2e8f0' }}>
            {order.limitPrice ? `$${order.limitPrice}` : 'Market'}
          </div>
        </div>
        <div>
          <span style={{
            background: style.bg,
            color: style.color,
            padding: '0.2rem 0.5rem',
            borderRadius: '0.25rem',
            fontSize: '0.75rem',
            fontWeight: '600'
          }}>
            {order.status.replace('_', ' ').toUpperCase()}
          </span>
        </div>
        <div style={{ color: '#64748b', fontSize: '0.8rem' }}>
          {new Date(order.createdAt).toLocaleString()}
        </div>
        {canCancel && (
          <button
            onClick={() => onCancel(order.id)}
            style={{
              padding: '0.4rem 0.75rem',
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid #ef4444',
              borderRadius: '0.375rem',
              color: '#ef4444',
              fontSize: '0.75rem',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
        )}
      </div>
      
      {/* Mobile card layout */}
      <div className="order-row-mobile" style={{ display: 'none' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {order.side === 'buy' ? <TrendingUp size={14} color="#22c55e" /> : <TrendingDown size={14} color="#ef4444" />}
            <span style={{ color: order.side === 'buy' ? '#22c55e' : '#ef4444', fontWeight: '600' }}>
              {order.side.toUpperCase()} {order.symbol}
            </span>
          </div>
          <span style={{
            background: style.bg,
            color: style.color,
            padding: '0.2rem 0.5rem',
            borderRadius: '0.25rem',
            fontSize: '0.7rem',
            fontWeight: '600'
          }}>
            {order.status.replace('_', ' ').toUpperCase()}
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: canCancel ? '0.5rem' : 0 }}>
          <div style={{ color: '#64748b', fontSize: '0.75rem' }}>
            {order.qty} shares • {order.limitPrice ? `$${order.limitPrice}` : 'Market'} • {order.type}
          </div>
          <div style={{ color: '#64748b', fontSize: '0.7rem' }}>
            {new Date(order.createdAt).toLocaleDateString()} {new Date(order.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>
        {canCancel && (
          <button
            onClick={() => onCancel(order.id)}
            style={{
              width: '100%',
              padding: '0.4rem',
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid #ef4444',
              borderRadius: '0.375rem',
              color: '#ef4444',
              fontSize: '0.75rem',
              cursor: 'pointer'
            }}
          >
            Cancel Order
          </button>
        )}
      </div>
    </div>
  );
};

// ============================================
// TRADE TICKET MODAL
// ============================================
const TradeTicket = ({ symbol, quote, onClose, onSubmit, loading }) => {
  const [side, setSide] = useState('buy');
  const [orderType, setOrderType] = useState('market');
  const [quantity, setQuantity] = useState('');
  const [limitPrice, setLimitPrice] = useState('');
  const [error, setError] = useState(null);

  const estimatedCost = () => {
    const qty = parseFloat(quantity) || 0;
    const price = orderType === 'limit' 
      ? parseFloat(limitPrice) || 0 
      : (quote?.askPrice || quote?.price || 0);
    return qty * price;
  };

  const handleSubmit = () => {
    if (!quantity || parseFloat(quantity) <= 0) {
      setError('Enter a valid quantity');
      return;
    }
    if (orderType === 'limit' && (!limitPrice || parseFloat(limitPrice) <= 0)) {
      setError('Enter a valid limit price');
      return;
    }
    onSubmit({
      symbol,
      qty: parseFloat(quantity),
      side,
      type: orderType,
      limitPrice: orderType === 'limit' ? parseFloat(limitPrice) : undefined
    });
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.85)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      <div style={{
        background: '#1e293b',
        border: '1px solid #475569',
        borderRadius: '1rem',
        padding: '1.5rem',
        width: '100%',
        maxWidth: '400px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h3 style={{ color: '#e2e8f0', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <ShoppingCart size={20} />
            Trade {symbol}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: '1.5rem', cursor: 'pointer' }}>×</button>
        </div>

        {quote && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-around',
            background: 'rgba(15,23,42,0.6)',
            borderRadius: '0.5rem',
            padding: '0.75rem',
            marginBottom: '1rem'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: '#64748b', fontSize: '0.7rem' }}>BID</div>
              <div style={{ color: '#22c55e', fontWeight: '700' }}>${(quote.bidPrice || quote.price || 0).toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: '#64748b', fontSize: '0.7rem' }}>ASK</div>
              <div style={{ color: '#ef4444', fontWeight: '700' }}>${(quote.askPrice || quote.price || 0).toFixed(2)}</div>
            </div>
            {(!quote.bidPrice || !quote.askPrice) && (
              <div style={{ textAlign: 'center', gridColumn: '1/-1' }}>
                <div style={{ color: '#64748b', fontSize: '0.65rem' }}>Last price (market closed)</div>
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '1rem' }}>
          <button
            onClick={() => setSide('buy')}
            style={{
              padding: '0.75rem',
              background: side === 'buy' ? '#22c55e' : 'rgba(34,197,94,0.1)',
              border: side === 'buy' ? 'none' : '1px solid #22c55e',
              borderRadius: '0.5rem',
              color: side === 'buy' ? 'white' : '#22c55e',
              fontWeight: '700',
              cursor: 'pointer'
            }}
          >
            BUY
          </button>
          <button
            onClick={() => setSide('sell')}
            style={{
              padding: '0.75rem',
              background: side === 'sell' ? '#ef4444' : 'rgba(239,68,68,0.1)',
              border: side === 'sell' ? 'none' : '1px solid #ef4444',
              borderRadius: '0.5rem',
              color: side === 'sell' ? 'white' : '#ef4444',
              fontWeight: '700',
              cursor: 'pointer'
            }}
          >
            SELL
          </button>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label style={{ color: '#94a3b8', fontSize: '0.8rem', display: 'block', marginBottom: '0.5rem' }}>Order Type</label>
          <select
            value={orderType}
            onChange={(e) => setOrderType(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem',
              background: '#0f172a',
              border: '1px solid #475569',
              borderRadius: '0.5rem',
              color: '#e2e8f0'
            }}
          >
            <option value="market">Market</option>
            <option value="limit">Limit</option>
          </select>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label style={{ color: '#94a3b8', fontSize: '0.8rem', display: 'block', marginBottom: '0.5rem' }}>Quantity</label>
          <input
            type="number"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            placeholder="0"
            min="1"
            style={{
              width: '100%',
              padding: '0.75rem',
              background: '#0f172a',
              border: '1px solid #475569',
              borderRadius: '0.5rem',
              color: '#e2e8f0',
              fontSize: '1.25rem',
              fontWeight: '600'
            }}
          />
        </div>

        {orderType === 'limit' && (
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ color: '#94a3b8', fontSize: '0.8rem', display: 'block', marginBottom: '0.5rem' }}>Limit Price</label>
            <input
              type="number"
              value={limitPrice}
              onChange={(e) => setLimitPrice(e.target.value)}
              placeholder="0.00"
              step="0.01"
              style={{
                width: '100%',
                padding: '0.75rem',
                background: '#0f172a',
                border: '1px solid #475569',
                borderRadius: '0.5rem',
                color: '#e2e8f0',
                fontSize: '1.25rem'
              }}
            />
          </div>
        )}

        <div style={{
          background: 'rgba(15,23,42,0.6)',
          borderRadius: '0.5rem',
          padding: '1rem',
          marginBottom: '1rem'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#94a3b8' }}>Estimated {side === 'buy' ? 'Cost' : 'Proceeds'}</span>
            <span style={{ color: side === 'buy' ? '#ef4444' : '#22c55e', fontSize: '1.25rem', fontWeight: '700' }}>
              ${estimatedCost().toFixed(2)}
            </span>
          </div>
        </div>

        {error && (
          <div style={{
            background: 'rgba(239,68,68,0.1)',
            border: '1px solid #ef4444',
            borderRadius: '0.5rem',
            padding: '0.75rem',
            marginBottom: '1rem',
            color: '#ef4444',
            fontSize: '0.85rem'
          }}>
            {error}
          </div>
        )}

        <button
          onClick={handleSubmit}
          disabled={loading}
          style={{
            width: '100%',
            padding: '1rem',
            background: side === 'buy' 
              ? 'linear-gradient(135deg, #22c55e, #16a34a)'
              : 'linear-gradient(135deg, #ef4444, #dc2626)',
            border: 'none',
            borderRadius: '0.5rem',
            color: 'white',
            fontWeight: '700',
            fontSize: '1rem',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.6 : 1
          }}
        >
          {loading ? 'Processing...' : `${side === 'buy' ? 'Buy' : 'Sell'} ${symbol}`}
        </button>
      </div>
    </div>
  );
};

// ============================================
// MAIN TRADING DASHBOARD
// ============================================
const TradingDashboard = ({ userId, userEmail, initialSymbol }) => {
  // State
  const [activeTab, setActiveTab] = useState('overview');
  const [accountStatus, setAccountStatus] = useState(null);
  const [account, setAccount] = useState(null);
  const [positions, setPositions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tradeSymbol, setTradeSymbol] = useState(null);
  const [tradeQuote, setTradeQuote] = useState(null);
  const [orderLoading, setOrderLoading] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showQuickTrade, setShowQuickTrade] = useState(false);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);

  // ============================================
  // SCROLL TO TOP ON MOUNT
  // ============================================
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // ============================================
  // LOGIN CHECK - Verify user is authenticated
  // ============================================
  useEffect(() => {
    const checkAuth = () => {
      const storedUser = localStorage.getItem('investorq_user');
      
      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          if (parsedUser && parsedUser.id && String(parsedUser.id).trim() !== '') {
            setIsLoggedIn(true);
          } else {
            setIsLoggedIn(false);
          }
        } catch (e) {
          console.error('Error parsing user data:', e);
          setIsLoggedIn(false);
        }
      } else {
        setIsLoggedIn(false);
      }
      setCheckingAuth(false);
    };
    
    checkAuth();
  }, [userId]);

  // Get actual userId from localStorage (don't trust prop alone)
  const getActualUserId = () => {
    const storedUser = localStorage.getItem('investorq_user');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        return parsedUser?.id || null;
      } catch (e) {
        return null;
      }
    }
    return null;
  };

  const actualUserId = getActualUserId();

  // Check for initial symbol prop (passed from parent)
  useEffect(() => {
    if (initialSymbol) {
      handleTrade(initialSymbol);
    }
  }, [initialSymbol]);

  // Fetch account status
  const fetchAccountStatus = useCallback(async () => {
    if (!actualUserId) return; // Don't fetch without valid user
    
    try {
      const response = await fetch(`${API_URL}/broker/account/status?userId=${actualUserId}`);
      const data = await response.json();
      setAccountStatus(data);
      
      if (data.hasAccount && data.account?.status === 'ACTIVE') {
        await fetchAccountDetails();
      }
    } catch (err) {
      console.error('Account status error:', err);
    }
  }, [actualUserId]);

  // Fetch account details
  const fetchAccountDetails = useCallback(async () => {
    if (!actualUserId) return;
    
    try {
      const response = await fetch(`${API_URL}/broker/account/details?userId=${actualUserId}`);
      const data = await response.json();
      if (data.success) {
        setAccount(data.account);
      }
    } catch (err) {
      console.error('Account details error:', err);
    }
  }, [actualUserId]);

  // Fetch positions
  const fetchPositions = useCallback(async () => {
    if (!actualUserId) return;
    
    try {
      const response = await fetch(`${API_URL}/broker/trading/positions?userId=${actualUserId}`);
      const data = await response.json();
      if (data.success) {
        setPositions(data.positions || []);
      }
    } catch (err) {
      console.error('Positions error:', err);
    }
  }, [actualUserId]);

  // Fetch orders
  const fetchOrders = useCallback(async () => {
    if (!actualUserId) return;
    
    try {
      const response = await fetch(`${API_URL}/broker/trading/orders?userId=${actualUserId}&status=all`);
      const data = await response.json();
      if (data.success) {
        setOrders(data.orders || []);
      }
    } catch (err) {
      console.error('Orders error:', err);
    }
  }, [actualUserId]);

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchAccountStatus();
      if (accountStatus?.hasAccount && accountStatus?.account?.status === 'ACTIVE') {
        await Promise.all([fetchPositions(), fetchOrders()]);
      }
      setLoading(false);
    };
    loadData();
  }, [fetchAccountStatus, fetchPositions, fetchOrders, accountStatus?.hasAccount, accountStatus?.account?.status]);

  // Refresh all data
  const handleRefresh = async () => {
    setLoading(true);
    await Promise.all([
      fetchAccountStatus(),
      fetchAccountDetails(),
      fetchPositions(),
      fetchOrders()
    ]);
    setLoading(false);
  };

  // Auto-refresh every 30 seconds during market hours
  useEffect(() => {
    const isMarketHours = () => {
      const now = new Date();
      const et = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
      const day = et.getDay();
      const hour = et.getHours();
      const minute = et.getMinutes();
      const timeInMinutes = hour * 60 + minute;
      // Mon-Fri, 9:30am - 4:00pm ET
      return day >= 1 && day <= 5 && timeInMinutes >= 570 && timeInMinutes <= 960;
    };

    const interval = setInterval(() => {
      if (accountStatus?.hasAccount && accountStatus?.account?.status === 'ACTIVE') {
        fetchAccountDetails();
        fetchPositions();
      }
    }, isMarketHours() ? 30000 : 60000); // 30s during market, 60s after hours

    return () => clearInterval(interval);
  }, [fetchAccountDetails, fetchPositions, accountStatus?.hasAccount, accountStatus?.account?.status]);

  // Handle trade
  const handleTrade = async (symbol) => {
    // Don't open modal until we have a quote — avoids $0 flash
    setTradeQuote(null);
    setQuoteLoading(true);
    // Fetch quote - try Alpaca first, fallback to Alpha Vantage
    try {
      const response = await fetch(`${API_URL}/trading/quote/${symbol}`);
      const data = await response.json();
      if (data.success && data.quote) {
        setTradeQuote(data.quote);
        setTradeSymbol(symbol); // open modal only after quote ready
      } else {
        // Fallback: fetch from Alpha Vantage via stock endpoint
        const avResponse = await fetch(`${API_URL}/stock/${symbol}`);
        const avData = await avResponse.json();
        if (avData && avData.currentPrice) {
          setTradeQuote({
            symbol: symbol.toUpperCase(),
            price: avData.currentPrice,
            bidPrice: avData.currentPrice * 0.999,
            askPrice: avData.currentPrice * 1.001
          });
          setTradeSymbol(symbol);
        }
      }
    } catch (err) {
      console.error('Quote error:', err);
      // Try Alpha Vantage as last resort
      try {
        const avResponse = await fetch(`${API_URL}/stock/${symbol}`);
        const avData = await avResponse.json();
        if (avData && avData.currentPrice) {
          setTradeQuote({
            symbol: symbol.toUpperCase(),
            price: avData.currentPrice,
            bidPrice: avData.currentPrice * 0.999,
            askPrice: avData.currentPrice * 1.001
          });
          setTradeSymbol(symbol);
        }
      } catch (e) {
        console.error('Fallback quote error:', e);
      }
    } finally {
      setQuoteLoading(false);
    }
  };

  // Submit order
  const handleSubmitOrder = async (orderData) => {
    setOrderLoading(true);
    try {
      const response = await fetch(`${API_URL}/broker/trading/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, ...orderData })
      });
      const data = await response.json();
      
      if (data.success) {
        setTradeSymbol(null);
        setTradeQuote(null);
        // Immediate refresh of positions and orders
        await Promise.all([fetchPositions(), fetchOrders()]);
        // Alpaca needs ~1-2s to process the order before account balance updates
        setTimeout(async () => {
          await fetchAccountDetails();
        }, 1500);
        // Second refresh after 5s to catch any settlement updates
        setTimeout(async () => {
          await Promise.all([fetchAccountDetails(), fetchPositions()]);
        }, 5000);
      } else {
        throw new Error(data.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setOrderLoading(false);
    }
  };

  // Cancel order
  const handleCancelOrder = async (orderId) => {
    if (!actualUserId) return;
    
    try {
      await fetch(`${API_URL}/broker/trading/orders/${orderId}?userId=${actualUserId}`, {
        method: 'DELETE'
      });
      await fetchOrders();
    } catch (err) {
      setError(err.message);
    }
  };

  // Calculate portfolio stats
  const portfolioStats = {
    totalValue: positions.reduce((sum, p) => sum + (p.marketValue || 0), 0),
    totalPL: positions.reduce((sum, p) => sum + (p.unrealizedPL || 0), 0),
    dayPL: account ? (account.equity - account.lastEquity) : 0
  };

  // Show loading while checking auth
  if (checkingAuth) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '400px' 
      }}>
        <Loader size={48} color="#6366f1" style={{ animation: 'spin 1s linear infinite' }} />
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Not logged in - show login required screen
  if (!isLoggedIn || !actualUserId) {
    return (
      <div style={{ 
        maxWidth: '500px', 
        margin: '0 auto', 
        padding: '2rem',
        minHeight: 'calc(100vh - 200px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          background: 'rgba(30, 41, 59, 0.95)',
          borderRadius: '1rem',
          padding: '2rem 1.5rem',
          textAlign: 'center',
          width: '100%'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #f59e0b, #d97706)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1.5rem'
          }}>
            <User size={40} color="white" />
          </div>
          
          <h2 style={{ color: '#e2e8f0', margin: '0 0 0.5rem', fontSize: '1.5rem' }}>
            Login Required
          </h2>
          
          <p style={{ color: '#94a3b8', marginBottom: '1.5rem', lineHeight: '1.6' }}>
            Please log in to your InvestorQ account to access the Trading Dashboard.
          </p>
          
          <div style={{
            background: 'rgba(96, 165, 250, 0.1)',
            border: '1px solid rgba(96, 165, 250, 0.3)',
            borderRadius: '0.75rem',
            padding: '1rem',
            marginBottom: '1.5rem',
            textAlign: 'left'
          }}>
            <p style={{ color: '#93c5fd', margin: 0, fontSize: '0.9rem' }}>
              <strong>Why do I need to log in?</strong>
            </p>
            <ul style={{ color: '#94a3b8', margin: '0.5rem 0 0', paddingLeft: '1.25rem', fontSize: '0.85rem', lineHeight: '1.6' }}>
              <li>Access your trading account securely</li>
              <li>View your portfolio and positions</li>
              <li>Execute trades with your brokerage account</li>
              <li>Manage your funds and transfers</li>
            </ul>
          </div>
          
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('openAuthModal'))}
            style={{
              width: '100%',
              padding: '1rem',
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              border: 'none',
              borderRadius: '0.5rem',
              color: 'white',
              fontWeight: '700',
              fontSize: '1rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem'
            }}
          >
            <User size={20} />
            Log In to Continue
          </button>
          
          <p style={{ color: '#64748b', margin: '1rem 0 0', fontSize: '0.8rem' }}>
            Don't have an account?{' '}
            <button 
              onClick={() => window.dispatchEvent(new CustomEvent('openAuthModal', { detail: { mode: 'register' } }))}
              style={{ 
                background: 'none', 
                border: 'none', 
                color: '#818cf8', 
                cursor: 'pointer',
                padding: 0
              }}
            >
              Sign up for free
            </button>
          </p>
        </div>
      </div>
    );
  }

  // No account - show onboarding prompt
  if (!loading && !accountStatus?.hasAccount) {
    return (
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '1rem' }}>
        <style>{`
          @media (max-width: 640px) {
            .trust-partners-row { flex-direction: column !important; gap: 1rem !important; }
            .onboarding-cta-card { padding: 1.5rem !important; }
            .onboarding-cta-card h2 { font-size: 1.35rem !important; }
          }
        `}</style>
        {showOnboarding ? (
          <BrokerOnboarding 
            userId={actualUserId}
            userEmail={userEmail}
            onComplete={() => {
              setShowOnboarding(false);
              fetchAccountStatus();
            }}
            onCancel={() => setShowOnboarding(false)}
          />
        ) : (
          <div>
            {/* Main CTA Card */}
            <div className="onboarding-cta-card" style={{
              background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
              border: '1px solid #334155',
              borderRadius: '1rem',
              padding: '2rem',
              textAlign: 'center',
              marginBottom: '1.5rem'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem'
              }}>
                <Zap size={40} color="white" />
              </div>
              
              <h2 style={{ color: '#e2e8f0', margin: '0 0 0.5rem', fontSize: '1.75rem' }}>
                Start Trading with InvestorQ
              </h2>
              <p style={{ color: '#94a3b8', margin: '0 0 2rem', maxWidth: '450px', marginLeft: 'auto', marginRight: 'auto' }}>
                Open your brokerage account in minutes. Commission-free stock trading with industry-leading security.
              </p>
              
              {/* Feature Grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
                gap: '1rem',
                marginBottom: '2rem',
                maxWidth: '650px',
                margin: '0 auto 2rem'
              }}>
                {[
                  { icon: DollarSign, label: 'Commission Free', desc: '$0 trades' },
                  { icon: Shield, label: 'SIPC Protected', desc: 'Up to $500K' },
                  { icon: Zap, label: 'Instant Trades', desc: 'Real-time execution' },
                  { icon: Award, label: 'Fractional Shares', desc: 'Start with $1' }
                ].map((item, idx) => (
                  <div key={idx} style={{
                    background: 'rgba(99, 102, 241, 0.1)',
                    border: '1px solid rgba(99, 102, 241, 0.3)',
                    borderRadius: '0.75rem',
                    padding: '1rem',
                    textAlign: 'center'
                  }}>
                    <item.icon size={24} color="#818cf8" style={{ marginBottom: '0.5rem' }} />
                    <div style={{ color: '#e2e8f0', fontSize: '0.9rem', fontWeight: '600' }}>
                      {item.label}
                    </div>
                    <div style={{ color: '#64748b', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                      {item.desc}
                    </div>
                  </div>
                ))}
              </div>
              
              <button
                onClick={() => setShowOnboarding(true)}
                style={{
                  padding: '1rem 3rem',
                  background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                  border: 'none',
                  borderRadius: '0.75rem',
                  color: 'white',
                  fontWeight: '700',
                  fontSize: '1.1rem',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  boxShadow: '0 4px 15px rgba(99, 102, 241, 0.4)'
                }}
              >
                Open Account
                <ChevronRight size={20} />
              </button>
              
              <p style={{ color: '#64748b', fontSize: '0.8rem', marginTop: '1rem' }}>
                ⚡ Takes about 5 minutes • No minimum deposit required
              </p>
            </div>

            {/* Powered By Partners Section */}
            <div style={{
              background: 'rgba(30, 41, 59, 0.6)',
              border: '1px solid #334155',
              borderRadius: '1rem',
              padding: '1.5rem',
              marginBottom: '1rem'
            }}>
              <div style={{
                textAlign: 'center',
                marginBottom: '1rem'
              }}>
                <span style={{
                  color: '#64748b',
                  fontSize: '0.7rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  fontWeight: '600'
                }}>
                  Powered By Trusted Partners
                </span>
              </div>
              
              <div className="trust-partners-row" style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '2rem',
                flexWrap: 'wrap'
              }}>
                {/* Broker-Dealer - Work in Progress */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  background: '#1e293b',
                  borderRadius: '0.75rem',
                  padding: '0.75rem 1.25rem',
                  border: '1px solid #334155'
                }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: '10px',
                    background: 'rgba(99,102,241,0.15)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '1.3rem'
                  }}>🔧</div>
                  <div>
                    <div style={{ 
                      color: '#818cf8', 
                      fontWeight: '700', 
                      fontSize: '1rem',
                      fontFamily: 'system-ui, -apple-system, sans-serif',
                    }}>
                      Broker-Dealer
                    </div>
                    <div style={{ color: '#94a3b8', fontSize: '0.7rem' }}>Partnership in Progress</div>
                  </div>
                </div>

                {/* Plaid - Official wordmark style */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  background: '#1e293b',
                  borderRadius: '0.75rem',
                  padding: '0.75rem 1.25rem',
                  border: '1px solid #334155'
                }}>
                  {/* Plaid Logo - Black background with wordmark */}
                  <svg width="40" height="40" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect width="100" height="100" rx="20" fill="#111111"/>
                    <text x="50" y="62" 
                      fill="white" 
                      fontSize="32" 
                      fontWeight="700" 
                      fontFamily="system-ui, -apple-system, sans-serif"
                      textAnchor="middle"
                      letterSpacing="-1">
                      plaid
                    </text>
                  </svg>
                  <div>
                    <div style={{ 
                      color: '#ffffff', 
                      fontWeight: '700', 
                      fontSize: '1.2rem',
                      fontFamily: 'system-ui, -apple-system, sans-serif',
                      letterSpacing: '-0.02em'
                    }}>
                      plaid
                    </div>
                    <div style={{ color: '#94a3b8', fontSize: '0.7rem' }}>Secure Bank Linking</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Compliance Badges */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '1rem',
              flexWrap: 'wrap',
              marginBottom: '1rem'
            }}>
              {[
                { label: 'FINRA', sublabel: 'Member', color: '#60a5fa', bg: 'rgba(96, 165, 250, 0.1)', border: 'rgba(96, 165, 250, 0.3)' },
                { label: 'SIPC', sublabel: 'Protected', color: '#22c55e', bg: 'rgba(34, 197, 94, 0.1)', border: 'rgba(34, 197, 94, 0.3)' },
                { label: 'SEC', sublabel: 'Registered', color: '#a78bfa', bg: 'rgba(139, 92, 246, 0.1)', border: 'rgba(139, 92, 246, 0.3)' },
                { label: '256-bit', sublabel: 'Encryption', color: '#fbbf24', bg: 'rgba(251, 191, 36, 0.1)', border: 'rgba(251, 191, 36, 0.3)' }
              ].map((badge, idx) => (
                <div key={idx} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  background: badge.bg,
                  border: `1px solid ${badge.border}`,
                  borderRadius: '0.5rem',
                  padding: '0.5rem 0.75rem'
                }}>
                  <CheckCircle size={14} color={badge.color} />
                  <div>
                    <div style={{ color: badge.color, fontSize: '0.7rem', fontWeight: '700' }}>{badge.label}</div>
                    <div style={{ color: badge.color, fontSize: '0.6rem', opacity: 0.8 }}>{badge.sublabel}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Legal Disclosure */}
            <div style={{
              textAlign: 'center',
              padding: '1rem',
              color: '#64748b',
              fontSize: '0.7rem',
              lineHeight: '1.6'
            }}>
              Brokerage services — broker-dealer partnership in progress. 
              Securities trading involves risk. Bank linking powered by Plaid with bank-level encryption.
            </div>
          </div>
        )}
      </div>
    );
  }

  // Account pending approval
  if (!loading && accountStatus?.hasAccount && accountStatus?.account?.status !== 'ACTIVE') {
    return (
      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '2rem' }}>
        <div style={{
          background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
          border: '1px solid #334155',
          borderRadius: '1rem',
          padding: '3rem',
          textAlign: 'center'
        }}>
          <Clock size={64} color="#fbbf24" style={{ marginBottom: '1rem' }} />
          <h2 style={{ color: '#e2e8f0', margin: '0 0 0.5rem' }}>
            Account Under Review
          </h2>
          <AccountStatusBadge status={accountStatus.account.status} />
          <p style={{ color: '#94a3b8', margin: '1rem 0 0' }}>
            Your application is being reviewed. This typically takes 1-3 business days.
            You'll receive an email once approved.
          </p>
        </div>
      </div>
    );
  }

  // Main dashboard
  return (
    <div className="trading-main-container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '1rem', overflowX: 'hidden' }}>
      {/* Mobile Responsive Styles */}
      <style>{`
        * { box-sizing: border-box; }
        @media (max-width: 640px) {
          .position-row-desktop { display: none !important; }
          .position-row-mobile { display: block !important; }
          .order-row-desktop { display: none !important; }
          .order-row-mobile { display: block !important; }
          .table-header-desktop { display: none !important; }
          .trust-partners-row { flex-direction: column !important; gap: 1rem !important; }
          .trust-partners-row > div:nth-child(2) { display: none !important; }
          .trust-badges-row { gap: 0.5rem !important; }
          .trust-badges-row > div { flex: 1 1 calc(50% - 0.5rem) !important; min-width: 120px !important; }
          .header-stats-row { display: grid !important; grid-template-columns: 1fr 1fr 1fr !important; gap: 0.5rem !important; width: 100% !important; }
          .header-stats-row > div { text-align: center !important; padding: 0.5rem 0.5rem !important; }
          .header-stats-row .stat-value { font-size: 1rem !important; }
          .header-stats-row .stat-label { font-size: 0.6rem !important; }
          .overview-grid { grid-template-columns: 1fr !important; gap: 1rem !important; }
          .overview-grid > div { min-width: 0 !important; overflow: hidden !important; }
          .tab-scroll-container { -webkit-overflow-scrolling: touch; scrollbar-width: none; }
          .tab-scroll-container::-webkit-scrollbar { display: none; }
          .positions-summary-row { flex-direction: column !important; gap: 0.25rem !important; align-items: flex-start !important; }
          .legal-links-row { flex-direction: column !important; gap: 0.5rem !important; }
          .legal-links-row span { display: none !important; }
          .trading-main-container { padding: 0.5rem !important; overflow-x: hidden !important; }
          .trading-header { padding: 1rem !important; border-radius: 0.75rem !important; margin-bottom: 1rem !important; }
          .trading-header h1 { font-size: 1.25rem !important; }
          .trading-section-card { padding: 1rem !important; }
          .positions-tab-card { padding: 1rem !important; }
        }
        @media (max-width: 768px) {
          .position-row-desktop { display: none !important; }
          .position-row-mobile { display: block !important; }
          .order-row-desktop { display: none !important; }
          .order-row-mobile { display: block !important; }
          .table-header-desktop { display: none !important; }
        }
      `}</style>
      {/* Header */}
      <div className="trading-header" style={{
        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
        borderRadius: '1rem',
        padding: '1.5rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h1 style={{ color: 'white', margin: 0, fontSize: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Zap size={28} />
              Trading Dashboard
            </h1>
            <p style={{ color: 'rgba(255,255,255,0.8)', margin: '0.25rem 0 0' }}>
              Welcome back! Your account is active.
            </p>
          </div>
          
          <div className="header-stats-row" style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <div style={{
              background: 'rgba(255,255,255,0.15)',
              borderRadius: '0.75rem',
              padding: '0.75rem 1.25rem',
              textAlign: 'center'
            }}>
              <div className="stat-label" style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.7rem' }}>Portfolio Value</div>
              <div className="stat-value" style={{ color: 'white', fontSize: '1.25rem', fontWeight: '700' }}>
                ${(account?.portfolioValue || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </div>
            </div>
            <div style={{
              background: 'rgba(255,255,255,0.15)',
              borderRadius: '0.75rem',
              padding: '0.75rem 1.25rem',
              textAlign: 'center'
            }}>
              <div className="stat-label" style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.7rem' }}>Buying Power</div>
              <div className="stat-value" style={{ color: 'white', fontSize: '1.25rem', fontWeight: '700' }}>
                ${(account?.buyingPower || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </div>
            </div>
            <div style={{
              background: 'rgba(255,255,255,0.15)',
              borderRadius: '0.75rem',
              padding: '0.75rem 1.25rem',
              textAlign: 'center'
            }}>
              <div className="stat-label" style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.7rem' }}>Day's P/L</div>
              <div className="stat-value" style={{ 
                color: portfolioStats.dayPL >= 0 ? '#86efac' : '#fca5a5',
                fontSize: '1.25rem',
                fontWeight: '700'
              }}>
                {portfolioStats.dayPL >= 0 ? '+' : ''}${portfolioStats.dayPL.toFixed(2)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Trade Button Bar */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', alignItems: 'center' }}>
        <button
          onClick={() => setShowQuickTrade(true)}
          disabled={quoteLoading}
          style={{
            padding: '0.6rem 1.5rem',
            background: quoteLoading ? '#065f46' : 'linear-gradient(135deg, #10b981, #059669)',
            border: 'none',
            borderRadius: '0.5rem',
            color: 'white',
            fontWeight: '700',
            fontSize: '0.9rem',
            cursor: quoteLoading ? 'wait' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.4rem',
            boxShadow: '0 2px 8px rgba(16,185,129,0.4)'
          }}
        >
          <TrendingUp size={16} /> {quoteLoading ? 'Loading...' : 'Trade'}
        </button>
        <div style={{ color: '#64748b', fontSize: '0.8rem' }}>
          {new Date().toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit' })} ET
          {' · '}Auto-refresh: 30s
        </div>
      </div>

      {/* Tabs */}
      <div className="tab-scroll-container" style={{
        display: 'flex',
        gap: '0.5rem',
        marginBottom: '1.5rem',
        overflowX: 'auto',
        paddingBottom: '0.5rem'
      }}>
        {[
          { id: 'overview', label: 'Overview', icon: PieChart },
          { id: 'positions', label: 'Positions', icon: Briefcase },
          { id: 'orders', label: 'Orders', icon: Clock },
          { id: 'analytics', label: 'Analytics', icon: BarChart3 },
          { id: 'rebalancer', label: 'Rebalancer', icon: Scale },
          { id: 'whatif', label: 'What-If', icon: Lightbulb },
          { id: 'risk', label: 'Risk', icon: AlertTriangle },
          { id: 'funding', label: 'Funding', icon: Wallet }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '0.6rem 1rem',
              background: activeTab === tab.id ? '#6366f1' : 'rgba(30, 41, 59, 0.6)',
              border: activeTab === tab.id ? 'none' : '1px solid #334155',
              borderRadius: '0.5rem',
              color: activeTab === tab.id ? 'white' : '#94a3b8',
              fontWeight: '600',
              fontSize: '0.85rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              whiteSpace: 'nowrap',
              flexShrink: 0
            }}
          >
            <tab.icon size={18} />
            {tab.label}
          </button>
        ))}
        
        <button
          onClick={handleRefresh}
          disabled={loading}
          style={{
            marginLeft: 'auto',
            padding: '0.75rem',
            background: 'rgba(30, 41, 59, 0.6)',
            border: '1px solid #334155',
            borderRadius: '0.5rem',
            color: '#94a3b8',
            cursor: 'pointer'
          }}
        >
          <RefreshCw size={18} className={loading ? 'spin' : ''} />
        </button>
      </div>

      {/* Error */}
      {error && (
        <div style={{
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid #ef4444',
          borderRadius: '0.5rem',
          padding: '0.75rem 1rem',
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          color: '#ef4444'
        }}>
          <AlertCircle size={18} />
          {error}
          <button onClick={() => setError(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }}>×</button>
        </div>
      )}

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="overview-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
          {/* Quick Trade */}
          <QuickTradeCard onTrade={handleTrade} />
          
          {/* Portfolio Summary */}
          <div className="trading-section-card" style={{
            background: 'rgba(30, 41, 59, 0.6)',
            border: '1px solid #334155',
            borderRadius: '0.75rem',
            padding: '1.25rem',
            minWidth: 0,
            overflow: 'hidden'
          }}>
            <h3 style={{ color: '#e2e8f0', margin: '0 0 1rem', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <PieChart size={18} />
              Portfolio Allocation
            </h3>
            {positions.length > 0 ? (
              <PortfolioDonut positions={positions} />
            ) : (
              <div style={{ color: '#64748b', textAlign: 'center', padding: '2rem' }}>
                No positions yet
              </div>
            )}
          </div>
          
          {/* Recent Orders */}
          <div className="trading-section-card" style={{
            background: 'rgba(30, 41, 59, 0.6)',
            border: '1px solid #334155',
            borderRadius: '0.75rem',
            padding: '1.25rem',
            minWidth: 0,
            overflow: 'hidden'
          }}>
            <h3 style={{ color: '#e2e8f0', margin: '0 0 1rem', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <History size={18} />
              Recent Orders
            </h3>
            {orders.slice(0, 3).map((order, idx) => (
              <div key={idx} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0.5rem 0',
                borderBottom: idx < 2 ? '1px solid #334155' : 'none'
              }}>
                <div>
                  <span style={{ color: order.side === 'buy' ? '#22c55e' : '#ef4444', fontWeight: '600' }}>
                    {order.side.toUpperCase()}
                  </span>
                  <span style={{ color: '#e2e8f0', marginLeft: '0.5rem' }}>{order.symbol}</span>
                </div>
                <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>{order.status}</span>
              </div>
            ))}
            {orders.length === 0 && (
              <div style={{ color: '#64748b', textAlign: 'center', padding: '1rem' }}>
                No orders yet
              </div>
            )}
          </div>
          
          {/* Account Summary */}
          <div className="trading-section-card" style={{
            background: 'rgba(30, 41, 59, 0.6)',
            border: '1px solid #334155',
            borderRadius: '0.75rem',
            padding: '1.25rem',
            minWidth: 0,
            overflow: 'hidden'
          }}>
            <h3 style={{ color: '#e2e8f0', margin: '0 0 1rem', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Wallet size={18} />
              Account Summary
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#94a3b8' }}>Cash</span>
                <span style={{ color: '#22c55e', fontWeight: '600' }}>${account?.cash?.toFixed(2) || '0.00'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#94a3b8' }}>Invested</span>
                <span style={{ color: '#e2e8f0', fontWeight: '600' }}>${portfolioStats.totalValue.toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#94a3b8' }}>Unrealized P/L</span>
                <span style={{ color: portfolioStats.totalPL >= 0 ? '#22c55e' : '#ef4444', fontWeight: '600' }}>
                  {portfolioStats.totalPL >= 0 ? '+' : ''}${portfolioStats.totalPL.toFixed(2)}
                </span>
              </div>
            </div>
            <button
              onClick={() => setActiveTab('funding')}
              style={{
                width: '100%',
                marginTop: '1rem',
                padding: '0.75rem',
                background: 'linear-gradient(135deg, #0f766e, #14b8a6)',
                border: 'none',
                borderRadius: '0.5rem',
                color: 'white',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
            >
              <Plus size={18} />
              Add Funds
            </button>
          </div>
        </div>
      )}

      {/* Positions Tab */}
      {activeTab === 'positions' && (
        <div className="positions-tab-card" style={{
          background: 'rgba(30, 41, 59, 0.6)',
          border: '1px solid #334155',
          borderRadius: '0.75rem',
          padding: '1.5rem',
          minWidth: 0,
          overflow: 'hidden'
        }}>
          <div className="positions-summary-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ color: '#e2e8f0', margin: 0, fontSize: '1.1rem' }}>Your Positions</h3>
            <div style={{ color: '#94a3b8', fontSize: '0.9rem' }}>
              Total: ${portfolioStats.totalValue.toFixed(2)} • P/L: 
              <span style={{ color: portfolioStats.totalPL >= 0 ? '#22c55e' : '#ef4444', marginLeft: '0.25rem' }}>
                {portfolioStats.totalPL >= 0 ? '+' : ''}${portfolioStats.totalPL.toFixed(2)}
              </span>
            </div>
          </div>
          
          {positions.length > 0 ? (
            <div>
              <div className="table-header-desktop" style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr 1fr auto',
                gap: '1rem',
                padding: '0.5rem 0',
                borderBottom: '2px solid #334155',
                color: '#64748b',
                fontSize: '0.75rem',
                fontWeight: '600'
              }}>
                <div>SYMBOL</div>
                <div>PRICE</div>
                <div style={{ textAlign: 'right' }}>VALUE</div>
                <div style={{ textAlign: 'right' }}>P/L</div>
                <div></div>
              </div>
              {positions.map((pos, idx) => (
                <PositionRow key={idx} position={pos} onTrade={handleTrade} />
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>
              <Briefcase size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
              <p>No positions yet. Start trading to build your portfolio!</p>
            </div>
          )}
        </div>
      )}

      {/* Orders Tab */}
      {activeTab === 'orders' && (
        <div className="positions-tab-card" style={{
          background: 'rgba(30, 41, 59, 0.6)',
          border: '1px solid #334155',
          borderRadius: '0.75rem',
          padding: '1.5rem',
          minWidth: 0,
          overflow: 'hidden'
        }}>
          <h3 style={{ color: '#e2e8f0', margin: '0 0 1rem' }}>Order History</h3>
          
          {orders.length > 0 ? (
            <div>
              <div className="table-header-desktop" style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr 1fr auto',
                gap: '1rem',
                padding: '0.5rem 0',
                borderBottom: '2px solid #334155',
                color: '#64748b',
                fontSize: '0.75rem',
                fontWeight: '600'
              }}>
                <div>ORDER</div>
                <div>PRICE</div>
                <div>STATUS</div>
                <div>TIME</div>
                <div></div>
              </div>
              {orders.map((order, idx) => (
                <OrderRow key={idx} order={order} onCancel={handleCancelOrder} />
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>
              <Clock size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
              <p>No orders yet.</p>
            </div>
          )}
        </div>
      )}

      {/* Funding Tab */}
      {activeTab === 'funding' && (
        <BankFunding userId={actualUserId} />
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <div>
          {positions.length > 0 ? (
            <AdvancedPortfolioAnalytics 
              portfolio={positions.map(p => ({
                symbol: p.symbol,
                shares: parseFloat(p.quantity) || parseFloat(p.qty) || 0,
                purchase_price: parseFloat(p.avgEntryPrice) || parseFloat(p.avg_entry_price) || 0,
                current_price: parseFloat(p.currentPrice) || parseFloat(p.current_price) || 0
              }))}
            />
          ) : (
            <div style={{
              background: 'rgba(30, 41, 59, 0.6)',
              border: '1px solid #334155',
              borderRadius: '0.75rem',
              padding: '3rem',
              textAlign: 'center'
            }}>
              <BarChart3 size={48} color="#64748b" style={{ marginBottom: '1rem' }} />
              <h3 style={{ color: '#e2e8f0', margin: '0 0 0.5rem' }}>No Positions Yet</h3>
              <p style={{ color: '#94a3b8', margin: 0 }}>
                Place some trades to see portfolio analytics.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Rebalancer Tab */}
      {activeTab === 'rebalancer' && (
        <div>
          {positions.length > 0 ? (
            <PortfolioRebalancer 
              user={{ id: actualUserId }}
              livePositions={positions.map(p => ({
                symbol: p.symbol,
                shares: parseFloat(p.quantity) || parseFloat(p.qty) || 0,
                purchase_price: parseFloat(p.avgEntryPrice) || parseFloat(p.avg_entry_price) || 0,
                current_price: parseFloat(p.currentPrice) || parseFloat(p.current_price) || 0,
                market_value: parseFloat(p.marketValue) || parseFloat(p.market_value) || 0
              }))}
              isLiveTrading={true}
            />
          ) : (
            <div style={{
              background: 'rgba(30, 41, 59, 0.6)',
              border: '1px solid #334155',
              borderRadius: '0.75rem',
              padding: '3rem',
              textAlign: 'center'
            }}>
              <Scale size={48} color="#64748b" style={{ marginBottom: '1rem' }} />
              <h3 style={{ color: '#e2e8f0', margin: '0 0 0.5rem' }}>No Positions to Rebalance</h3>
              <p style={{ color: '#94a3b8', margin: 0 }}>
                Build your portfolio first, then use the rebalancer to optimize allocations.
              </p>
            </div>
          )}
        </div>
      )}

      {/* What-If Tab */}
      {activeTab === 'whatif' && (
        <div>
          <WhatIfAnalyzer 
            realPortfolio={{
              holdings: positions.map(p => ({
                symbol: p.symbol,
                shares: parseFloat(p.quantity) || 0,
                purchasePrice: parseFloat(p.avgEntryPrice) || 0,
                currentPrice: parseFloat(p.currentPrice) || 0,
                marketValue: parseFloat(p.marketValue) || 0,
                unrealizedPL: parseFloat(p.unrealizedPL) || 0
              })),
              totalValue: positions.reduce((sum, p) => sum + (parseFloat(p.marketValue) || 0), 0)
            }}
            onExecuteInPaperTrading={null}
          />
        </div>
      )}

      {/* Risk Tab */}
      {activeTab === 'risk' && (
        <div>
          {positions.length > 0 ? (
            <PortfolioRiskAnalyzer 
              portfolio={positions.map(p => ({
                symbol: p.symbol,
                shares: parseFloat(p.quantity) || parseFloat(p.qty) || 0,
                costBasis: parseFloat(p.avgEntryPrice) || parseFloat(p.avg_entry_price) || 0,
                current_price: parseFloat(p.currentPrice) || parseFloat(p.current_price) || 0
              }))}
            />
          ) : (
            <div style={{
              background: 'rgba(30, 41, 59, 0.6)',
              border: '1px solid #334155',
              borderRadius: '0.75rem',
              padding: '3rem',
              textAlign: 'center'
            }}>
              <AlertTriangle size={48} color="#64748b" style={{ marginBottom: '1rem' }} />
              <h3 style={{ color: '#e2e8f0', margin: '0 0 0.5rem' }}>No Positions to Analyze</h3>
              <p style={{ color: '#94a3b8', margin: 0 }}>
                Add positions to your portfolio to see risk analysis.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Quick Trade Search Overlay */}
      {showQuickTrade && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
          zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center'
        }} onClick={() => setShowQuickTrade(false)}>
          <div style={{
            background: '#1e293b', border: '1px solid #334155', borderRadius: '1rem',
            padding: '1.5rem', width: '400px', maxWidth: '90vw'
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ color: '#e2e8f0', margin: 0 }}>Quick Trade</h3>
              <button onClick={() => setShowQuickTrade(false)} style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: '1.5rem', cursor: 'pointer' }}>×</button>
            </div>
            <StockSearch
              onSelectStock={(symbol) => { setShowQuickTrade(false); handleTrade(symbol); }}
              placeholder="Search stocks to trade..."
            />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '1rem' }}>
              {['AAPL','TSLA','NVDA','MSFT','AMZN','GOOGL','META','SPY'].map(s => (
                <button key={s} onClick={() => { setShowQuickTrade(false); handleTrade(s); }}
                  style={{ padding: '0.4rem 0.75rem', background: '#334155', border: 'none', borderRadius: '0.375rem', color: '#94a3b8', fontSize: '0.8rem', cursor: 'pointer' }}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Trade Modal */}
      {tradeSymbol && (
        <TradeTicket
          symbol={tradeSymbol}
          quote={tradeQuote}
          onClose={() => {
            setTradeSymbol(null);
            setTradeQuote(null);
          }}
          onSubmit={handleSubmitOrder}
          loading={orderLoading}
        />
      )}

      {/* Disclosure */}
      <TrustSection />
    </div>
  );
};

// ============================================
// TRUST & SECURITY SECTION
// Professional partner logos and compliance badges
// ============================================
const TrustSection = () => {
  return (
    <div style={{
      marginTop: '2rem',
      background: 'linear-gradient(180deg, rgba(30, 41, 59, 0.6) 0%, rgba(15, 23, 42, 0.8) 100%)',
      borderRadius: '1rem',
      border: '1px solid #334155',
      overflow: 'hidden'
    }}>
      {/* Powered By Section */}
      <div style={{
        padding: '1.5rem',
        borderBottom: '1px solid #334155'
      }}>
        <div style={{
          textAlign: 'center',
          marginBottom: '1rem'
        }}>
          <span style={{
            color: '#64748b',
            fontSize: '0.7rem',
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            fontWeight: '600'
          }}>
            Powered By Industry Leaders
          </span>
        </div>
        
        <div className="trust-partners-row" style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '3rem',
          flexWrap: 'wrap'
        }}>
          {/* Broker-Dealer Logo & Info */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <div style={{
              background: '#1e293b',
              borderRadius: '0.75rem',
              padding: '0.75rem 1.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              border: '1px solid #334155'
            }}>
              <div style={{
                width: 36, height: 36, borderRadius: '8px',
                background: 'rgba(99,102,241,0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '1.2rem'
              }}>🔧</div>
              <div>
                <div style={{ 
                  color: '#818cf8', 
                  fontWeight: '700', 
                  fontSize: '1.1rem',
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                }}>Broker-Dealer</div>
                <div style={{ color: '#94a3b8', fontSize: '0.65rem' }}>Partnership in Progress</div>
              </div>
            </div>
            <span style={{ color: '#64748b', fontSize: '0.7rem' }}>Brokerage Services</span>
          </div>

          {/* Divider */}
          <div style={{
            width: '1px',
            height: '50px',
            background: 'linear-gradient(180deg, transparent, #475569, transparent)'
          }} />

          {/* Plaid Logo & Info - Official style */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <div style={{
              background: '#1e293b',
              borderRadius: '0.75rem',
              padding: '0.75rem 1.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              border: '1px solid #334155'
            }}>
              {/* Plaid Logo - Black with wordmark */}
              <svg width="36" height="36" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="100" height="100" rx="20" fill="#111111"/>
                <text x="50" y="62" 
                  fill="white" 
                  fontSize="32" 
                  fontWeight="700" 
                  fontFamily="system-ui, -apple-system, sans-serif"
                  textAnchor="middle"
                  letterSpacing="-1">
                  plaid
                </text>
              </svg>
              <div>
                <div style={{ 
                  color: '#ffffff', 
                  fontWeight: '700', 
                  fontSize: '1.1rem',
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                  letterSpacing: '-0.02em'
                }}>plaid</div>
                <div style={{ color: '#94a3b8', fontSize: '0.65rem' }}>Secure Banking</div>
              </div>
            </div>
            <span style={{ color: '#64748b', fontSize: '0.7rem' }}>Bank Connections</span>
          </div>
        </div>
      </div>

      {/* Trust Badges */}
      <div style={{
        padding: '1.25rem',
        background: 'rgba(15, 23, 42, 0.5)',
        borderBottom: '1px solid #334155'
      }}>
        <div className="trust-badges-row" style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '1.5rem',
          flexWrap: 'wrap'
        }}>
          {/* SIPC Badge */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            background: 'rgba(34, 197, 94, 0.1)',
            border: '1px solid rgba(34, 197, 94, 0.3)',
            borderRadius: '0.5rem',
            padding: '0.5rem 0.75rem'
          }}>
            <Shield size={16} color="#22c55e" />
            <div>
              <div style={{ color: '#22c55e', fontSize: '0.75rem', fontWeight: '700' }}>SIPC</div>
              <div style={{ color: '#86efac', fontSize: '0.6rem' }}>Protected</div>
            </div>
          </div>

          {/* FINRA Badge */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            background: 'rgba(96, 165, 250, 0.1)',
            border: '1px solid rgba(96, 165, 250, 0.3)',
            borderRadius: '0.5rem',
            padding: '0.5rem 0.75rem'
          }}>
            <CheckCircle size={16} color="#60a5fa" />
            <div>
              <div style={{ color: '#60a5fa', fontSize: '0.75rem', fontWeight: '700' }}>FINRA</div>
              <div style={{ color: '#93c5fd', fontSize: '0.6rem' }}>Member</div>
            </div>
          </div>

          {/* SEC Registered Badge */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            background: 'rgba(139, 92, 246, 0.1)',
            border: '1px solid rgba(139, 92, 246, 0.3)',
            borderRadius: '0.5rem',
            padding: '0.5rem 0.75rem'
          }}>
            <Award size={16} color="#a78bfa" />
            <div>
              <div style={{ color: '#a78bfa', fontSize: '0.75rem', fontWeight: '700' }}>SEC</div>
              <div style={{ color: '#c4b5fd', fontSize: '0.6rem' }}>Registered</div>
            </div>
          </div>

          {/* Bank-Level Security Badge */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            background: 'rgba(251, 191, 36, 0.1)',
            border: '1px solid rgba(251, 191, 36, 0.3)',
            borderRadius: '0.5rem',
            padding: '0.5rem 0.75rem'
          }}>
            <CreditCard size={16} color="#fbbf24" />
            <div>
              <div style={{ color: '#fbbf24', fontSize: '0.75rem', fontWeight: '700' }}>256-bit</div>
              <div style={{ color: '#fde68a', fontSize: '0.6rem' }}>Encryption</div>
            </div>
          </div>
        </div>
      </div>

      {/* Legal Disclosure */}
      <div style={{
        padding: '1rem 1.5rem',
        textAlign: 'center'
      }}>
        <p style={{
          color: '#64748b',
          fontSize: '0.7rem',
          lineHeight: '1.6',
          margin: 0
        }}>
          Brokerage services — <strong style={{ color: '#94a3b8' }}>broker-dealer partnership in progress</strong>. 
          Securities trading involves risk and may result in loss of principal. 
          Bank account linking powered by <strong style={{ color: '#94a3b8' }}>Plaid Inc.</strong> with bank-level security.
          Your personal and financial information is encrypted and never stored on our servers.
        </p>
        <div className="legal-links-row" style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '1rem',
          marginTop: '0.75rem'
        }}>
          <a 
            href="/disclosures.html" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ color: '#6366f1', fontSize: '0.7rem', textDecoration: 'none' }}
          >
            InvestorQ Disclosures
          </a>
          <span style={{ color: '#475569' }}>•</span>
          <a 
            href="https://plaid.com/legal" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ color: '#6366f1', fontSize: '0.7rem', textDecoration: 'none' }}
          >
            Plaid Privacy
          </a>
          <span style={{ color: '#475569' }}>•</span>
          <a 
            href="https://www.finra.org/brokercheck" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ color: '#6366f1', fontSize: '0.7rem', textDecoration: 'none' }}
          >
            FINRA BrokerCheck
          </a>
        </div>
      </div>
    </div>
  );
};

export default TradingDashboard;